// app/components/LazyHydrate.tsx

"use client";

import { useEffect, useRef, useState } from "react";
import type { ComponentType, Key, ReactNode } from "react";
import { useIntersectionOnce } from "@/lib/useIntersectionOnce";

interface LazyHydrateProps<P> {
  /** Dynamic-import callback, e.g. () => import("./Chart") */
  loader: () => Promise<{ default: ComponentType<P> }>;
  /** Placeholder while the dynamic chunk loads */
  fallback?: ReactNode;
  /** Start pre-loading when the placeholder is this far from view */
  rootMargin?: string;
  /** Standard IntersectionObserver threshold(s) */
  threshold?: number | number[];
}

export default function LazyHydrate<P extends Record<string, unknown>>(
  props: LazyHydrateProps<P> & P & { key?: Key }
) {
  const {
    loader,
    fallback = null,
    rootMargin = "600px",
    threshold = 0,
    ...rest
  } = props;

  const [holderRef, visible] = useIntersectionOnce<HTMLDivElement>({
    rootMargin,
    threshold,
  });
  const [Inner, setInner] = useState<ComponentType<P> | null>(null);
  const loadPromiseRef = useRef<Promise<{ default: ComponentType<P> }> | null>(
    null
  );

  /* --- visibility watcher -------------------------------------------- */
  useEffect(() => {
    if (!visible || Inner) return;
    if (!loadPromiseRef.current) {
      loadPromiseRef.current = loader();
    }

    let cancelled = false;
    loadPromiseRef.current.then((m) => {
      if (!cancelled) setInner(() => m.default);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, Inner, loader]);

  /* --- render --------------------------------------------------------- */
  return (
    <div ref={holderRef}>
      {Inner ? <Inner {...(rest as P)} /> : fallback}
    </div>
  );
}
